import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  ApiError,
  api,
  capabilityAct,
  noDirectory,
  roleCatalogue,
  roleNames,
  shortDate,
  signedInRoleCodes,
  type Capability,
  type Me,
  type Person,
  type Persona,
  type PersonaCatalogue,
  type RoleEntry,
} from '../lib/api'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { phoneWords } from '../lib/people'
import { resetFooter } from '../components/footers'
import { Dialog, Status, Toggletip, type Tone } from '../components/kit'
import { useSave } from '../lib/save'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/persona.css'
import '../styles/sweep.css'

// The people who sign this tenant in, and the screen that adds one.
//
// WHY THIS EXISTS AS A SCREEN AT ALL
//
// Section 2 gives the System Administrator configuration, users, master data
// and workflows. Users was the one the product did not do: adding a colleague
// meant editing a terraform variable and running an apply, so a joiner cost a
// deployment and the only people who could give somebody a role were the people
// who held the AWS account.
//
// WHY THE TABLE BELOW IS NOT A TABLE THIS PRODUCT KEEPS
//
// There is no users table. Every row here was read from the identity provider a
// moment ago and every button writes back to it. A copy would drift, and it
// would drift in the one direction that matters: somebody removed from the
// provider on their last day would still be a person in this product's list.
//
// WHY A GREYED OUT ROLE IS THE SERVER'S ANSWER AND NOT THIS SCREEN'S
//
// Whether somebody may hand a role to a colleague is a question about
// capabilities: they may when every capability the role holds is one their own
// roles already carry, and when they administer users at all. This screen used
// to work that out for itself by comparing role codes, which is a different
// rule and a wrong one. It greyed out every box but System Administrator for a
// System Administrator, who by the matrix staffs procurement, management and
// the read-only codes, so the one screen section 2 gives that role could do
// almost nothing.
//
// GET /v1/roles answers it per row now, for the sign-in that asked, and it is
// the same comparison POST /v1/users and PUT /v1/users/{subject}/roles make
// before they write. Everything below renders that answer and states no rule of
// its own.
//
// Taking a role away is gated the same way giving one is, so there is no
// one-way door here any more: a role this caller could not grant is one they
// cannot untick either, because a role nobody left can restore is not a role
// anybody should be able to remove.
//
// The screen needs no capability check of its own. user.administer opens it,
// and every route it calls declares the same one, so a reader who got here can
// use everything on it.
//
// ONE ACCOUNT, ONE PERSONA (15 September 2026)
//
// An administrator chooses the job, and the role follows from it. Several jobs
// share one role, so choosing roles could not say which job somebody was given,
// and a person holding two roles was two jobs on one account. The picker below
// offers personas, one per account; the role each brings is shown beside it and
// is not something to tick. An account that still does two jobs is marked
// "Split before production", because production refuses it at sign-in.

// The setting that decides whether this screen has anything to administer. The
// API names it too; it is repeated here because "not available" without a name
// sends an administrator to the wrong person. This is a deployment variable,
// not a permission and not an outage.

// The shape the API checks an invitation address against, so a typo is caught
// under the control it was typed into rather than coming back as a 400 from
// somewhere the reader has to go and find. Whether an address will actually
// receive anything is not something a regular expression decides.
const EMAIL_SHAPE = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/

// A WORK PHONE (review 3.4)
//
// The owner of a vendor is shown with their contact details, and the directory
// holds a name and an address and never a phone. So a work phone is typed
// here, the same ten-digit Indian entry the vendor form uses with +91 shown in
// front, and kept by this product beside the person. It is offered on a
// roster the platform manages too: it changes nothing there, and nowhere else
// holds it.
const INDIAN_NUMBER = /^[1-9][0-9]{9}$/

// The country code, shown and never typed, as on the vendor form.
const PHONE_PREFIX = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 9px',
  border: '1px solid var(--line)',
  borderRight: 0,
  borderRadius: 'var(--radius) 0 0 var(--radius)',
  background: 'var(--surface-2)',
  color: 'var(--ink-2)',
  fontVariantNumeric: 'tabular-nums',
} as const

// The ten digits of a stored Indian number, for the box. A number from
// anywhere else cannot be typed here, so the box starts empty and the number
// on file is shown beside it.
function tenDigits(e164: string | undefined): string {
  const m = /^\+91([1-9][0-9]{9})$/.exec(e164 ?? '')
  return m ? (m[1] as string) : ''
}

// Each account state is a kit label with its sentence on hover. A switched off
// account is a settled state and goes quiet; a state the provider could not
// name is the one that asks to be looked at.
const STATUS: Record<Person['status'], { label: string; tone: Tone; detail: string }> = {
  invited: {
    label: 'Invited',
    tone: 'info',
    detail:
      'The invitation has gone out and has not been finished with. A sign-in that stopped at the password change reads the same way, because what to do about either is the same: wait, or invite them again.',
  },
  confirmed: {
    label: 'Signing in',
    tone: 'ok',
    detail: 'The account is in use.',
  },
  disabled: {
    label: 'Switched off',
    tone: 'plain',
    detail: 'They cannot sign in. Everything they did before this is still theirs.',
  },
  unknown: {
    label: 'Not readable',
    tone: 'warn',
    detail:
      'The provider reports a state this build has no name for, and it is shown as that rather than guessed at. Somebody who is in fact archived or compromised must not be painted as active.',
  },
}

// Codes and subjects arrive from an identity provider, and providers are not
// consistent about case or trailing space. The API folds both, so this does.
function fold(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

// Why a box is greyed out.
//
// There is one reason, and the screen says it rather than reciting capability
// codes at somebody. Granting is a declared reach on the role: an administrator
// hands out the roles their own row names. For a System Administrator that is
// every role in the tenant except the TPRM Admin, which is the only role that
// can do everything including staffing the tenant, so handing it out is a
// decision for somebody who already holds it.
//
// The capability list is NOT the reason. It is what the person being created
// will be able to do that the caller cannot, and on this screen that is the
// ordinary case rather than an error: an administrator creates the Business
// Owner who closes findings and does not close findings. It is shown on rows
// that are open, under noteFor, so somebody handing out a role knows what they
// are handing out.
function whyLocked(role: RoleEntry, held: boolean): string {
  const mine = signedInRoleCodes()
  const wall =
    role.name +
    ' is not yours to hand out. Your sign-in carries ' +
    (mine.length > 0 ? mine.join(', ') : 'no roles at all') +
    ', and an administrator hands out the roles their own row names.'
  return held
    ? wall + ' The same wall stops you taking it away, because a role you could not give back is not one to remove.'
    : wall
}

// What handing this role out gives away, where that is more than the person
// doing it has. Empty on a role whose reach the caller already covers, which is
// most of them.
function noteFor(role: RoleEntry): string {
  const beyond = role.beyondYourReach ?? []
  if (beyond.length === 0) {
    return ''
  }
  return (
    'This role can do things your own sign-in cannot: ' +
    beyond.map((c) => capabilityAct(c) + ' (' + c + ')').join('; ') +
    '. That is the point of staffing a tenant, and it is worth knowing before you do it.'
  )
}

// One persona on the picker. reason is the whole sentence: why it is locked, or
// on an open row what handing out its role gives away. line is the few words
// the card shows for it, because a card that carries the sentence is a card
// nobody can scan, and the sentence is one press away behind the (i).
type PersonaChoice = {
  persona: Persona
  role: RoleEntry | undefined
  locked: boolean
  reason: string
  line: string
}

// The personas an administrator may choose between, for an invitation (current
// is empty) or for somebody who already holds roles.
//
// A persona is locked when its role is not the caller's to hand out, and also
// when saving it would take away a role the person holds that the caller could
// not give back: changing the persona replaces the roles, so both walls the
// server applies are met here before the save rather than after it.
function personaChoices(catalogue: PersonaCatalogue | null, current: string[]): PersonaChoice[] {
  if (!catalogue) return []
  const roles = roleCatalogue()
  const keptOutOfReach = roles.filter((r) => r.grantable === false && current.some((c) => fold(c, r.code)))
  return catalogue.personas
    .filter((ps) => ps.role)
    .map((ps) => {
      const role = roles.find((r) => fold(r.code, ps.role ?? ''))
      const blockedBy = keptOutOfReach.find((r) => !fold(r.code, ps.role ?? ''))
      if (role?.grantable === false) {
        return {
          persona: ps,
          role,
          locked: true,
          reason: whyLocked(role, current.some((c) => fold(c, role.code))),
          line: 'Not yours to hand out',
        }
      }
      if (blockedBy) {
        return {
          persona: ps,
          role,
          locked: true,
          reason:
            'They hold ' + blockedBy.name + ', and choosing this persona would take it away. ' + whyLocked(blockedBy, true),
          line: 'Would take away ' + blockedBy.name,
        }
      }
      const beyond = role?.beyondYourReach?.length ?? 0
      return {
        persona: ps,
        role,
        locked: false,
        reason: role ? noteFor(role) : '',
        line: beyond === 1 ? '1 ability beyond your own' : beyond + ' abilities beyond your own',
      }
    })
}

function PersonaPicker({
  name,
  catalogue,
  current,
  selected,
  busy,
  describedBy,
  onChoose,
}: {
  name: string
  catalogue: PersonaCatalogue | null
  current: string[]
  selected: string
  busy: boolean
  describedBy: string
  onChoose: (code: string) => void
}) {
  // Gated on both tables, for the reason the role picker was: a picker drawn
  // before GET /v1/roles answered would lock or unlock rows on a guess.
  if (!catalogue || roleCatalogue().length === 0) {
    return <Loading what="the personas" />
  }
  const rows = personaChoices(catalogue, current)
  return (
    <fieldset className="fieldset" aria-describedby={describedBy}>
      <legend>Persona</legend>
      <div className="persona-pick" role="radiogroup">
        {rows.map((row) => {
          const id = name + '-' + row.persona.code
          return (
            <label htmlFor={id} key={row.persona.code}>
              <input
                id={id}
                type="radio"
                name={name}
                value={row.persona.code}
                checked={selected === row.persona.code}
                disabled={busy || row.locked}
                aria-describedby={row.reason !== '' ? id + '-why' : undefined}
                onChange={() => onChoose(row.persona.code)}
              />
              <strong>{row.persona.name}</strong>
              <span className="persona-job">{row.persona.job}</span>
              <span className="persona-role">
                Role: {row.role?.name ?? row.persona.role} <span className="mono">{row.persona.role}</span>
              </span>
              {/* A locked card says so in a label and an open card counts what
                  it gives away; the sentence behind either is the (i). */}
              {row.reason !== '' && (
                <span className="field-locked sw-row" id={id + '-why'}>
                  {row.locked && <Status tone="neutral">Locked</Status>}
                  <span>{row.line}</span>
                  <ReasonTip
                    label={row.locked ? 'Why ' + row.persona.name + ' is locked' : 'What ' + row.persona.name + ' gives away'}
                    text={row.reason}
                  />
                </span>
              )}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

// What the chosen roles let somebody do, in the API's own words for each act
// and the API's own reason for each role. The console holds no copy of either:
// both tables arrive from GET /v1/roles, so what this paragraph promises and
// what the server will allow are one sentence rather than two that drift.
function Consequence({ id, selected }: { id: string; selected: string[] }) {
  const rows = roleCatalogue().filter((r) => selected.some((c) => fold(c, r.code)))

  const acts: Capability[] = []
  for (const r of rows) {
    for (const c of r.capabilities) {
      if (!acts.includes(c)) acts.push(c)
    }
  }

  if (rows.length === 0) {
    return null
  }

  return (
    <div id={id}>
      {/* The stem the API's own refusal uses, so the words under it are the words
          a 403 would have used about the same wall. */}
      <p className="outcome-head">The ability to</p>
      <ul className="rule-list">
        {acts.map((c) => (
          <li key={c}>
            {capabilityAct(c)} <span className="mono">{c}</span>
          </li>
        ))}
      </ul>
      <p className="outcome-head">Because they hold</p>
      <ul className="outcome-list">
        {rows.map((r) => (
          <li key={r.code}>
            <strong>{r.name}</strong>
            <span className="mono"> {r.code}</span>{' '}
            <ReasonTip label={'About ' + r.name} text={r.why} />
          </li>
        ))}
      </ul>
    </div>
  )
}

// The one role a persona brings, as the list the consequence panel reads.
function rolesOf(persona: Persona | undefined): string[] {
  return persona?.role ? [persona.role] : []
}

// A switch that failed, and which way it was going. One error state serves
// both buttons, so without this the verb was read off whichever confirmation
// happened to be open and a failed switch-off was re-rendered as an account
// that could not be switched on.
type SwitchFailure = { on: boolean; error: ApiError }

function switchVerb(f: SwitchFailure): string {
  return f.on ? 'switched on' : 'switched off'
}

export function Users() {
  const [people, setPeople] = useState<Person[] | null>(null)
  // Non-empty when the roster belongs to somebody else, and it holds the
  // sentence saying where. Emptiness is the test the buttons read, so there is
  // one fact rather than a flag and a string that can disagree.
  const [elsewhere, setElsewhere] = useState('')
  const [error, setError] = useState<unknown>(null)
  // The persona each person's roles derive, shown under their roles so an
  // administrator sees the job as well as the entitlement. Best effort: a
  // catalogue that cannot be read just leaves the line off.
  const [catalogue, setCatalogue] = useState<PersonaCatalogue | null>(null)
  useEffect(() => {
    api.personas().then(setCatalogue).catch(() => setCatalogue(null))
  }, [])
  const personaByCode = (code: string | undefined): Persona | undefined =>
    code ? catalogue?.personas.find((x) => x.code === code) : undefined
  // The persona a row shows. An API from before personas were stored sends
  // none, and the row then names what the roles derive, as it did before.
  const personaNames = (p: Person): string[] => {
    if (!catalogue) return []
    if (p.personas && p.personas.length > 0) {
      return p.personas.map((c) => personaByCode(c)?.name ?? c)
    }
    const names = new Set<string>()
    for (const r of p.roles) {
      const found = catalogue.personas.find((x) => x.code === catalogue.derivedFrom[r])
      if (found) names.add(found.name)
    }
    return [...names]
  }
  const [me, setMe] = useState<Me | null>(null)
  // TPRM's own accounts (AUTH_MODE=accounts): the account's name and whether
  // it may also use a TPRM password are chosen here.
  const accountsMode = me?.authMode === 'accounts'
  const [inviteName, setInviteName] = useState('')
  const [invitePassword, setInvitePassword] = useState(false)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [invitePersona, setInvitePersona] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<ApiError | null>(null)
  const [invited, setInvited] = useState<Person | null>(null)
  // When the persona stops granting anything (release 3.1): required for the
  // external auditor, 30 days when left blank, at most 180.
  const [inviteExpires, setInviteExpires] = useState('')

  const [editing, setEditing] = useState<Person | null>(null)
  const [editPersona, setEditPersona] = useState('')
  const [editExpires, setEditExpires] = useState('')
  const [saving, setSaving] = useState(false)
  // A second factor removed for a lost phone, and what it said. The person is
  // asked about first, in a dialog that names them and what removing it does.
  const [resetting, setResetting] = useState<Person | null>(null)
  const [resetAsked, setResetAsked] = useState('')
  const resetSave = useSave(resetting ? resetting.subject : null, 'Remove the second factor')
  const [resetSaid, setResetSaid] = useState('')
  const [resetError, setResetError] = useState<ApiError | null>(null)
  const [saveError, setSaveError] = useState<ApiError | null>(null)

  const [phoneFor, setPhoneFor] = useState<Person | null>(null)
  const [phoneDigits, setPhoneDigits] = useState('')
  const [phoneSaving, setPhoneSaving] = useState(false)
  const [phoneError, setPhoneError] = useState<ApiError | null>(null)
  const [phoneSaid, setPhoneSaid] = useState('')
  // Whether the box has been left, so a number still being typed is not
  // called wrong before it is finished. A leading 0 is wrong at once.
  const [phoneLeft, setPhoneLeft] = useState(false)

  const [armed, setArmed] = useState<Person | null>(null)
  const [switching, setSwitching] = useState(false)
  const [switchError, setSwitchError] = useState<SwitchFailure | null>(null)

  const editRef = useRef<HTMLElement | null>(null)
  const phoneRef = useRef<HTMLElement | null>(null)
  const armedRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(() => {
    api
      .users()
      .then((roster) => {
        setPeople(roster.people)
        setElsewhere(roster.managedElsewhere ? roster.why || 'These people are managed outside this product.' : '')
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(() => {
    load()
    // Which row is the reader's own. The API refuses a person changing their
    // own roles and refuses them switching their own account either way, and a
    // refusal a screen could have predicted should not be something it lets
    // somebody walk into. Switching yourself back on is refused for a reason
    // worth keeping in mind here: it is how somebody another administrator has
    // just switched off undoes that, while their token is still good.
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
  }, [load])

  // A panel that opens between the header and the table is a panel a keyboard
  // reader has already walked past, so focus moves to it rather than staying on
  // the row button that opened it.
  const editSubject = editing?.subject ?? ''
  const armedSubject = armed?.subject ?? ''
  const phoneSubject = phoneFor?.subject ?? ''
  useEffect(() => {
    if (editSubject !== '') editRef.current?.focus()
    else if (armedSubject !== '') armedRef.current?.focus()
    else if (phoneSubject !== '') phoneRef.current?.focus()
  }, [editSubject, armedSubject, phoneSubject])

  function asApiError(err: unknown): ApiError {
    return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
  }

  // The three writes below reload whatever happens, and one failure is the
  // reason. NOT_RECORDED means the provider took the change and the evidence
  // chain then refused it: the account really is different and the call really
  // did fail. Reloading only on success left the table showing the state before
  // a change that had already happened, which is the one wrong answer a screen
  // about who may do what must not give.
  function invite(e: FormEvent) {
    e.preventDefault()
    const persona = personaByCode(invitePersona)
    if (!persona?.role) return
    setInviting(true)
    setInviteError(null)
    api
      .inviteUser(
        email.trim(),
        persona.code,
        persona.role,
        accountsMode
          ? { name: inviteName.trim(), passwordSignIn: invitePassword, expiresAt: inviteExpires || undefined }
          : inviteExpires
            ? { expiresAt: inviteExpires }
            : undefined,
      )
      .then((person) => {
        setInvited(person)
        setEmail('')
        setInvitePersona('')
        setInviteExpires('')
        setInviteName('')
        setInvitePassword(false)
        setInviteOpen(false)
      })
      .catch((err) => setInviteError(asApiError(err)))
      .finally(() => {
        setInviting(false)
        load()
      })
  }

  function startEdit(person: Person) {
    setArmed(null)
    setPhoneFor(null)
    setSaveError(null)
    setEditing(person)
    // Only a persona an administrator chose is preselected. A persona derived
    // from roles is a guess about which of the jobs sharing a role was meant,
    // and saving a guess is how somebody ends up in the wrong job.
    setEditPersona(person.personaSet && person.persona ? person.persona : '')
    // The time box kept, so saving the same persona again without touching
    // the date keeps the engagement's end rather than removing it.
    setEditExpires(person.personaExpiresAt ? person.personaExpiresAt.slice(0, 10) : '')
  }

  // A lost phone: another administrator removes the person's second factor,
  // which ends their sessions; at their next sign-in they set up a new one.
  function askResetSecondFactor(person: Person) {
    setResetAsked(person.email || person.subject)
    setResetting(person)
  }

  // What follows the reset, in the person's words for whoever it was.
  function resetHandlers(person: Person) {
    const who = person.email || person.subject
    return {
      done: () => {
        setResetSaid('Second factor removed for ' + who + '. They set up a new one at their next sign-in.')
        setResetting(null)
        load()
      },
      failed: (err: unknown) => {
        setResetError(asApiError(err))
        setResetting(null)
        load()
      },
    }
  }


  function startPhone(person: Person) {
    setEditing(null)
    setArmed(null)
    setPhoneError(null)
    setPhoneSaid('')
    setPhoneFor(person)
    setPhoneDigits(tenDigits(person.phone))
    setPhoneLeft(false)
  }

  // An empty phone takes the number off. The server keeps it in E.164 and
  // answers with what it kept, and the roster is read again either way, as
  // the other writes on this screen do.
  function savePhone(phone: string) {
    if (phoneFor === null) return
    const who = phoneFor.email || phoneFor.subject
    setPhoneSaving(true)
    setPhoneError(null)
    api
      .setUserPhone(phoneFor.subject, phone)
      .then((r) => {
        setPhoneFor(null)
        setPhoneSaid(r.phone ? 'Work phone for ' + who + ' is ' + phoneWords(r.phone) : 'Work phone for ' + who + ' taken off')
      })
      .catch((err) => setPhoneError(asApiError(err)))
      .finally(() => {
        setPhoneSaving(false)
        load()
      })
  }

  function savePersona() {
    const persona = personaByCode(editPersona)
    if (editing === null || !persona?.role) return
    const subject = editing.subject
    const role = persona.role
    setSaving(true)
    setSaveError(null)
    api
      .setUserPersona(subject, persona.code, editExpires)
      .catch((err) => {
        // An API from before personas were stored has no such route and
        // answers with a status and no JSON. The persona's one role is then
        // set directly, which is the same change to the directory.
        if (err instanceof ApiError && (err.status === 404 || err.status === 405) && err.code !== 'NOT_FOUND') {
          return api.setUserRoles(subject, [role])
        }
        throw err
      })
      .then(() => setEditing(null))
      .catch((err) => setSaveError(asApiError(err)))
      .finally(() => {
        setSaving(false)
        load()
      })
  }

  // TPRM's own accounts: a TPRM password on or off for one account.
  function switchPassword(person: Person, allowed: boolean) {
    setSwitching(true)
    setSwitchError(null)
    api
      .setPasswordSignIn(person.subject, allowed)
      .catch((err) => setSwitchError({ on: allowed, error: asApiError(err) }))
      .finally(() => {
        setSwitching(false)
        load()
      })
  }

  function switchAccount(person: Person, on: boolean) {
    setSwitching(true)
    setSwitchError(null)
    const call = on ? api.enableUser(person.subject) : api.disableUser(person.subject)
    call
      .then(() => setArmed(null))
      .catch((err) => setSwitchError({ on, error: asApiError(err) }))
      .finally(() => {
        setSwitching(false)
        load()
      })
  }

  const columns: Column<Person>[] = [
    {
      key: 'person',
      header: 'Person',
      sortValue: (p) => p.email,
      render: (p) => (
        <>
          <span className="cell-primary">{p.email || 'no address recorded'}</span>
          {p.name && <span className="cell-sub">{p.name}</span>}
          {accountsMode && (
            <span className="cell-sub">
              {signInWords(p)}
              {p.disabledReason ? '. ' + p.disabledReason : ''}
            </span>
          )}
          {p.phone && <span className="cell-sub">{phoneWords(p.phone)}</span>}
          <span className="cell-sub mono">{p.subject}</span>
        </>
      ),
    },
    {
      key: 'persona',
      header: 'Persona',
      sortValue: (p) => personaNames(p).join(', ') || roleNames(p.roles).join(', '),
      render: (p) =>
        p.roles.length === 0 && personaNames(p).length === 0 ? (
          <span className="muted">none, so they sign in and read nothing</span>
        ) : (
          <span className="persona-cell">
            <span>{personaNames(p).join(' and ') || 'No persona'}</span>
            {p.personaExpiresAt && (
              <span className="chiprow">
                <Status tone={p.personaExpired ? 'crit' : 'warn'} title="When this persona stops granting anything (release 3.1). Save the persona again with a new date to extend it.">
                  {(p.personaExpired ? 'Ended ' : 'Until ') + shortDate(p.personaExpiresAt)}
                </Status>
              </span>
            )}
            {/* The role is the consequence of the persona, shown and not edited. */}
            <span className="muted">
              {roleNames(p.roles).join(', ') || 'no role'}
              {p.personaSet === false && personaNames(p).length > 0 ? ', from their roles' : ''}
            </span>
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (p) => (p.splitBeforeProduction ? 'a' : '') + p.status,
      render: (p) => (
        <span className="chiprow">
          <Status tone={STATUS[p.status].tone} title={STATUS[p.status].detail}>
            {STATUS[p.status].label}
          </Status>
          {p.splitBeforeProduction && (
            <Status tone="warn" title="This account does more than one job. Production refuses it at sign-in until each job has its own account.">
              Split before production
            </Status>
          )}
        </span>
      ),
    },
    {
      key: 'changed',
      header: 'Last changed',
      sortValue: (p) => p.updatedAt,
      render: (p) => (p.updatedAt ? shortDate(p.updatedAt) : <span className="muted">not reported</span>),
    },
    {
      key: 'act',
      header: 'Change',
      render: (p) => {
        const self = me !== null && fold(p.subject, me.subject)
        const whyId = 'self-' + p.subject
        // Only the phone on a roster somebody else owns: the persona and the
        // switch are theirs, and a disabled button would still say this is the
        // place. The phone is this product's own and kept nowhere else.
        const phoneButton = (
          <button type="button" className="linkish" onClick={() => startPhone(p)}>
            Phone
          </button>
        )
        if (elsewhere !== '') {
          return (
            <span className="chiprow">
              {phoneButton}
              <span className="muted">Managed elsewhere</span>
            </span>
          )
        }
        return (
          <>
            <span className="chiprow">
              <button
                type="button"
                className="linkish"
                disabled={self}
                aria-describedby={self ? whyId : undefined}
                onClick={() => startEdit(p)}
              >
                Persona
              </button>
              {phoneButton}
              {accountsMode && (
                <button
                  type="button"
                  className="linkish"
                  disabled={self || switching}
                  aria-describedby={self ? whyId : undefined}
                  onClick={() => switchPassword(p, !p.passwordAllowed)}
                >
                  {p.passwordAllowed ? 'SSO only' : 'Allow password'}
                </button>
              )}
              {accountsMode && p.secondFactor && (
                <button
                  type="button"
                  className="linkish"
                  data-action="tprm.users.a07"
                  disabled={self}
                  aria-describedby={self ? whyId : undefined}
                  onClick={() => askResetSecondFactor(p)}
                  title="For a lost phone: removes their second factor and ends their sessions"
                >
                  Reset second factor
                </button>
              )}
              {p.enabled ? (
                <button
                  type="button"
                  className="linkish"
                  disabled={self || switching}
                  aria-describedby={self ? whyId : undefined}
                  onClick={() => {
                    setEditing(null)
                    setPhoneFor(null)
                    setSwitchError(null)
                    setArmed(p)
                  }}
                >
                  Switch off
                </button>
              ) : (
                <button
                  type="button"
                  className="linkish"
                  data-action="tprm.users.a05"
                  disabled={self || switching}
                  aria-describedby={self ? whyId : undefined}
                  onClick={() => switchAccount(p, true)}
                >
                  Switch on
                </button>
              )}
            </span>
            {/* A label rather than a sentence under every button, with the rule
                one press away. */}
            {self && (
              <p className="field-locked sw-row" id={whyId} style={{ marginTop: 3 }}>
                <Status tone="neutral">This is you</Status>
                <Toggletip label="Why you cannot change your own account">
                  Nobody changes their own persona, switches their own account off, or switches it back on after another
                  administrator switched it off; ask somebody else.
                </Toggletip>
              </p>
            )}
          </>
        )
      },
    },
  ]

  const heading = (
    <div className="page-head">
      <h1>People</h1>
    </div>
  )

  if (error !== null && noDirectory(error)) {
    return (
      <>
        {heading}
        <div className="note stop" role="status">
          <strong>People are managed in your sign-in provider</strong>
          <p className="sw-row">
            <span>This local build has no sign-in provider</span>
            <Toggletip label="When people appear here">They appear here once they have signed in.</Toggletip>
          </p>
        </div>
      </>
    )
  }

  if (error !== null) return <ErrorBox error={error} what="The people in this tenant" />
  if (people === null) return <Loading what="the people in this tenant" />

  const phoneWrong =
    phoneDigits.startsWith('0') || (phoneLeft && phoneDigits !== '' && !INDIAN_NUMBER.test(phoneDigits))

  const typed = email.trim()
  const emailWrong = typed !== '' && !EMAIL_SHAPE.test(typed)
  const invitedCount = people.filter((p) => p.status === 'invited').length
  const offCount = people.filter((p) => !p.enabled).length

  return (
    <>
      {heading}

      {invited !== null && (
        <div className="ok-note" role="status">
          <h3>{invited.email} has an account</h3>
          {/* The two facts an administrator acts on stay as lines; what to do if
              the password never arrives, and what the subject is for, are tips. */}
          {accountsMode ? (
            <p className="sw-row" style={{ marginBottom: 6 }}>
              <span>
                They sign in with single sign-on
                {invited.passwordAllowed ? ', or a TPRM password they set from the sign-in page' : ''}
              </span>
              <Toggletip label="Why no password is sent">
                TPRM never sends a password. A welcome message with the address to sign in at is on its
                way; a password, if allowed, is set by the person with the code the sign-in page mails them.
              </Toggletip>
            </p>
          ) : (
            <p className="sw-row" style={{ marginBottom: 6 }}>
              <span>The provider is sending the temporary password now</span>
              <Toggletip label="If the password does not arrive">
                Ask the provider to send it again: it cannot be recovered or repeated from here.
              </Toggletip>
            </p>
          )}
          <p className="muted sw-row">
            <span>
              Subject <span className="mono">{invited.subject}</span>
            </span>
            <Toggletip label="What the subject is for">
              It is the name every evidence entry will use for what they do.
            </Toggletip>
            <button type="button" className="linkish" onClick={() => setInvited(null)}>
              Dismiss
            </button>
          </p>
        </div>
      )}

      {elsewhere !== '' && (
        <div className="note" role="status">
          <strong>These people are managed outside this product</strong>
          {/* Where they are managed is the API's sentence and is kept whole
              behind the (i); what that means for this screen is one line. */}
          <p className="sw-row">
            <span>Add, change and switch off people there, changes show here within a minute</span>
            <ReasonTip label="Where these people are managed" text={elsewhere} />
          </p>
        </div>
      )}

      {elsewhere === '' && (
      <section className="panel" aria-labelledby="invite-heading">
        <div className="exc-raise-head">
          <h2 id="invite-heading" style={{ margin: 0 }}>
            Invite somebody
          </h2>
          <button type="button" aria-expanded={inviteOpen} onClick={() => setInviteOpen((v) => !v)}>
            {inviteOpen ? 'Close' : 'New person'}
          </button>
        </div>

        {inviteOpen && (
          <form onSubmit={invite}>
            {inviteError !== null && <ErrorBox error={inviteError} what="The invitation" verb="sent" />}

            <div className="field">
              <label htmlFor="invite-email">Email address</label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={'invite-email-note' + (emailWrong ? ' invite-email-issue' : '')}
              />
              {emailWrong && (
                <p className="field-error" id="invite-email-issue">
                  The invitation is delivered to this address, so it has to be one.
                </p>
              )}
            </div>

            {accountsMode && (
              <>
                <div className="field">
                  <label htmlFor="invite-name">Name</label>
                  <input
                    id="invite-name"
                    type="text"
                    value={inviteName}
                    autoComplete="off"
                    onChange={(e) => setInviteName(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>
                    <input
                      type="checkbox"
                      checked={invitePassword}
                      onChange={(e) => setInvitePassword(e.target.checked)}
                    />{' '}
                    May also sign in with a TPRM password
                  </label>
                  <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                    Otherwise single sign-on only. They set the password themselves from the sign-in page; nobody
                    here sees it.
                  </p>
                </div>
              </>
            )}

            <PersonaPicker
              name="invite-persona"
              catalogue={catalogue}
              current={[]}
              selected={invitePersona}
              busy={inviting}
              describedBy="invite-consequence"
              onChoose={setInvitePersona}
            />
            <Consequence id="invite-consequence" selected={rolesOf(personaByCode(invitePersona))} />
            <ExpiryField id="invite-expires" persona={invitePersona} value={inviteExpires} onChange={setInviteExpires} />

            <div className="commit-bar">
              <button
                type="submit"
                className="primary"
                data-action="tprm.users.a01"
                disabled={inviting || typed === '' || emailWrong || invitePersona === ''}
              >
                {inviting ? 'Inviting' : 'Invite'}
              </button>
              <button type="button" onClick={() => setInviteOpen(false)} disabled={inviting}>
                Cancel
              </button>
              <span className="muted">
                {invitePersona === ''
                  ? 'Choose the one persona they work as'
                  : 'Their role follows from the persona and is given when the account is created'}
              </span>
            </div>
          </form>
        )}
      </section>
      )}

      {editing !== null && (
        <section className="panel" aria-labelledby="edit-heading" ref={editRef} tabIndex={-1}>
          <div className="panel-head">
            <h2 id="edit-heading" style={{ margin: 0 }}>
              Persona for {editing.email || editing.subject}
            </h2>
            <span className="muted mono">{editing.subject}</span>
          </div>

          <div style={{ padding: '12px 16px 0' }}>
            {saveError !== null && <ErrorBox error={saveError} what="The persona" verb="changed" />}

            {editing.splitBeforeProduction && (
              <p className="note warn sw-row" role="note" style={{ marginBottom: 10 }}>
                <Status tone="warn">Two jobs</Status>
                <span>{personaNames(editing).join(' and ')}</span>
                <Toggletip label="How to split this account">
                  Choose the one it keeps, then invite a separate account for the other job.
                </Toggletip>
              </p>
            )}
            <PersonaPicker
              name="edit-persona"
              catalogue={catalogue}
              current={editing.roles}
              selected={editPersona}
              busy={saving}
              describedBy="edit-consequence"
              onChoose={setEditPersona}
            />
            <Consequence id="edit-consequence" selected={rolesOf(personaByCode(editPersona))} />
            <ExpiryField id="edit-expires" persona={editPersona} value={editExpires} onChange={setEditExpires} />
          </div>

          <div className="commit-bar">
            <button
              type="button"
              className="primary"
              data-action="tprm.users.a03"
              data-action-also="tprm.users.a04"
              onClick={savePersona}
              disabled={saving || editPersona === ''}
            >
              {saving ? 'Saving' : 'Save persona'}
            </button>
            <button type="button" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
            <span className="muted">
              {editPersona === ''
                ? 'Choose the one persona this account works as'
                : 'Their roles become this persona\'s role, any other role is taken away'}
            </span>
          </div>
        </section>
      )}

      {resetSaid !== '' && (
        <div className="ok-note" role="status">
          <p className="sw-row" style={{ margin: 0 }}>
            <span>{resetSaid}</span>
            <button type="button" className="linkish" onClick={() => setResetSaid('')}>
              Dismiss
            </button>
          </p>
        </div>
      )}
      {resetError !== null && <ErrorBox error={resetError} what="The second factor" verb="removed" />}
      <Dialog
        shape="modal"
        kind="form"
        open={resetting !== null}
        onClose={() => setResetting(null)}
        title={'Remove the second factor of ' + resetAsked + '?'}
        {...resetFooter({ subject: resetting ? resetting.subject : null, busy: resetSave.busy }, (work) => {
          if (!resetting) return
          setResetSaid('')
          setResetError(null)
          resetSave.send(work, resetHandlers(resetting))
        })}
      >
        <p style={{ margin: 0 }}>Their sessions end now, and they set up a new one at their next sign-in.</p>
      </Dialog>

      {phoneSaid !== '' && (
        <div className="ok-note" role="status">
          <p className="sw-row" style={{ margin: 0 }}>
            <span>{phoneSaid}</span>
            <button type="button" className="linkish" onClick={() => setPhoneSaid('')}>
              Dismiss
            </button>
          </p>
        </div>
      )}

      {phoneFor !== null && (
        <section className="panel" aria-labelledby="phone-heading" ref={phoneRef} tabIndex={-1}>
          <div className="panel-head">
            <h2 id="phone-heading" style={{ margin: 0 }}>
              Work phone for {phoneFor.email || phoneFor.subject}
            </h2>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (INDIAN_NUMBER.test(phoneDigits)) savePhone('+91' + phoneDigits)
            }}
          >
            <div style={{ padding: '12px 16px 0' }}>
              {phoneError !== null && <ErrorBox error={phoneError} what="The work phone" verb="saved" />}
              <div className="field" style={{ maxWidth: 320 }}>
                <label htmlFor="phone-digits" className="sw-row">
                  <span>Work phone</span>
                  <Toggletip hover label="Where the work phone shows">
                    With their name and address wherever they are the owner of a vendor.
                  </Toggletip>
                </label>
                <div style={{ display: 'flex' }}>
                  <span aria-hidden="true" style={PHONE_PREFIX}>
                    +91
                  </span>
                  {/* No maxLength: a number pasted with its spaces is longer
                      than ten characters, and the box keeps its ten digits. */}
                  <input
                    id="phone-digits"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="off"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, minWidth: 0 }}
                    value={phoneDigits}
                    placeholder="9876543210"
                    aria-invalid={phoneWrong}
                    aria-describedby={'phone-cc' + (phoneWrong ? ' phone-issue' : '')}
                    onBlur={() => setPhoneLeft(true)}
                    onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  />
                  <span id="phone-cc" className="vh">
                    10 digits, +91 is added
                  </span>
                </div>
                {phoneWrong && (
                  <p className="field-error" id="phone-issue">
                    A work phone is 10 digits and does not start with 0
                  </p>
                )}
                {phoneFor.phone && tenDigits(phoneFor.phone) === '' && (
                  <p className="muted">On file: {phoneWords(phoneFor.phone)}</p>
                )}
              </div>
            </div>

            <div className="commit-bar">
              <button
                type="submit"
                className="primary"
                data-action="tprm.users.a02"
                disabled={phoneSaving || !INDIAN_NUMBER.test(phoneDigits)}
              >
                {phoneSaving ? 'Saving' : 'Save phone'}
              </button>
              {phoneFor.phone && (
                <button type="button" onClick={() => savePhone('')} disabled={phoneSaving}>
                  Take it off
                </button>
              )}
              <button type="button" onClick={() => setPhoneFor(null)} disabled={phoneSaving}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {armed !== null && (
        <div className="confirm" ref={armedRef} tabIndex={-1} style={{ marginBottom: 'var(--gap)' }}>
          <strong>Switch off {armed.email || armed.subject}?</strong>
          <p>
            They cannot sign in from their next attempt{' '}
            <Toggletip label="What happens to what they have done">
              Everything they have already done stays attributed to them: the evidence chain names their subject, and
              that name has to keep resolving long after they have gone.
            </Toggletip>
          </p>
          <p>Nothing is deleted, switching them back on is one button and loses nothing</p>
          {switchError !== null && (
            <ErrorBox error={switchError.error} what="The account" verb={switchVerb(switchError)} />
          )}
          <div className="chiprow">
            <button
              type="button"
              className="primary"
              data-action="tprm.users.a06"
              onClick={() => switchAccount(armed, false)}
              disabled={switching}
            >
              {switching ? 'Switching off' : 'Yes, switch off'}
            </button>
            <button type="button" onClick={() => setArmed(null)} disabled={switching}>
              Not now
            </button>
          </div>
        </div>
      )}

      {switchError !== null && armed === null && (
        <ErrorBox error={switchError.error} what="The account" verb={switchVerb(switchError)} />
      )}

      <DataTable
        caption="People who can sign in to this tenant"
        columns={columns}
        rows={people}
        rowKey={(p) => p.subject}
        defaultSort={{ key: 'person', dir: 'asc' }}
        empty={{
          title: 'Nobody is listed',
          detail: 'The directory returned no accounts for this tenant, yet you are signed in to it',
        }}
        footer={
          <ListCount shown={people.length} noun="people" one="person">
            , {invitedCount} {invitedCount === 1 ? 'invitation' : 'invitations'} still out, {offCount} switched off
          </ListCount>
        }
      />
    </>
  )
}

// How a TPRM account signs in, in words.
function signInWords(p: Person): string {
  const sso = p.ssoBound ? 'Single sign-on (used)' : 'Single sign-on'
  if (!p.passwordAllowed) return sso + ' only'
  return sso + ' or a TPRM password' + (p.passwordSet ? '' : ' (not set yet)')
}

// When the persona stops granting anything (release 3.1, persona spec T1 and
// decision F7). The external auditor is always time-boxed: blank is 30 days,
// the server refuses more than 180. Any other persona may be given an end.
function ExpiryField({ id, persona, value, onChange }: { id: string; persona: string; value: string; onChange: (v: string) => void }) {
  const external = persona === 'external_auditor'
  const today = new Date().toISOString().slice(0, 10)
  return (
    <div className="field" style={{ marginTop: 10 }}>
      <label htmlFor={id}>Access ends{external ? '' : ' (optional)'}</label>
      <input id={id} type="date" min={today} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={id + '-note'} />
      <p className="muted" id={id + '-note'} style={{ fontSize: 12, margin: '4px 0 0' }}>
        {external
          ? 'An external auditor is given access for the engagement: 30 days when left blank, at most 180. Save again with a new date to extend it.'
          : 'Leave blank for no end. On the date, the persona stops granting anything at sign-in and on every request.'}
      </p>
    </div>
  )
}
